import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  ListItemButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Search as SearchIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import ChatHistoryModal from '../chat/ChatHistoryModal';

const ContactsList = ({ chats }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChat, setSelectedChat] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const filteredChats = chats.filter(chat =>
    chat.phoneNumber && chat.phoneNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return 'Sin mensajes';
    
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Hoy';
    if (diffDays === 2) return 'Ayer';
    if (diffDays <= 7) return `Hace ${diffDays - 1} días`;
    
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getMessageCount = (messages) => {
    return messages ? messages.length : 0;
  };

  const handleContactClick = (chat) => {
    setSelectedChat(chat);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedChat(null);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Buscar contacto..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="small"
        sx={{ 
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: 'grey.50',
            '&:hover': {
              backgroundColor: 'grey.100',
            },
            '&.Mui-focused': {
              backgroundColor: 'background.paper',
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
      />
      


      <List sx={{ flex: 1, overflow: 'auto', px: 0 }}>
        {filteredChats.map((chat, index) => (
          <React.Fragment key={chat.id}>
            <ListItemButton 
              alignItems="flex-start"
              onClick={() => handleContactClick(chat)}
              sx={{
                borderRadius: 2,
                mb: 1,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  transform: 'translateY(-2px)',
                  boxShadow: '0px 4px 12px rgba(25, 118, 210, 0.15)',
                  borderColor: 'primary.main',
                },
                cursor: 'pointer',
              }}
            >
              <ListItemAvatar sx={{ mt: 0.5 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'primary.main',
                    width: 44,
                    height: 44,
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                sx={{ ml: 1 }}
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography 
                      variant="subtitle2" 
                      noWrap 
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        maxWidth: '60%'
                      }}
                    >
                      {chat.phoneNumber || chat.id}
                    </Typography>
                    <Chip
                      icon={<MessageIcon sx={{ fontSize: 14 }} />}
                      label={getMessageCount(chat.messages)}
                      size="small"
                      sx={{
                        backgroundColor: 'success.light',
                        color: 'success.contrastText',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography
                      component="div"
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      📅 {formatLastMessageTime(chat.lastMessageAt)}
                    </Typography>
                    <Typography
                      component="div"
                      variant="body2"
                      sx={{ 
                        color: 'primary.main',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        backgroundColor: 'transparent',
                        p: 0.8,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'primary.main',
                        },
                      }}
                    >
                      💬 Click para ver {getMessageCount(chat.messages)} mensajes
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </React.Fragment>
        ))}
        
        {filteredChats.length === 0 && (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            sx={{ 
              height: 300,
              backgroundColor: 'grey.50',
              borderRadius: 2,
              border: '2px dashed',
              borderColor: 'grey.300',
            }}
          >
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="textSecondary" sx={{ mb: 1, fontWeight: 500 }}>
              {searchTerm ? 'Sin resultados' : 'Sin contactos'}
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              {searchTerm ? 'Intenta con otro término de búsqueda' : 'Los contactos aparecerán aquí cuando haya actividad'}
            </Typography>
          </Box>
        )}
      </List>

      <ChatHistoryModal
        open={modalOpen}
        onClose={handleCloseModal}
        chat={selectedChat}
      />
    </Box>
  );
};

export default ContactsList;
